import {
  FIELD_COMPLIES_WITH_MINIMUM_STANDARDS,
  FIELD_DATE_OF_DRUG_USAGE,
  FIELD_DATE_OF_ELECTRICITY_CHECK,
  FIELD_DATE_OF_GAS_CHECK,
  FIELD_DATE_OF_HOMICIDE,
  FIELD_DATE_OF_POOL_CHECK,
  FIELD_DATE_OF_REPAIR_NOTICE,
  FIELD_EMBEDDED_NETWORK_ABN,
  FIELD_EMBEDDED_NETWORK_NAME,
  FIELD_EMBEDDED_NETWORK_PHONE,
  FIELD_EMBEDDED_NETWORK_PRICING,
  FIELD_EMBEDDED_NETWORK_WEBSITE,
  FIELD_FURTHER_INFORMATION,
  FIELD_HAS_ASBESTOS,
  FIELD_HAS_CONTRACT_OF_SALE_PREPARED,
  FIELD_HAS_DEFECT_SAFETY_CONCERN_NOTICE,
  FIELD_HAS_DOMESTIC_BUILDING_DISPUTE,
  FIELD_HAS_DRUG_USAGE_5YR,
  FIELD_HAS_EMBEDDED_NETWORK,
  FIELD_HAS_ENGAGED_AGENT_TO_SELL,
  FIELD_HAS_HOMICIDE,
  FIELD_HAS_MORTGAGEE_ACTION,
  FIELD_HAS_MOULD_DAMP_REPAIR_NOTICE_3YR,
  FIELD_HAS_OUTSTANDING_SAFETY_RECOMMENDATIONS,
  FIELD_HAS_OWNERS_CORPORATION_DISPUTE,
  FIELD_HAS_OWNERS_CORPORATION_RULES,
  FIELD_HAS_RIGHT_TO_RENT,
  FIELD_IS_AFFECTED_WITH_RELEVANT_AUTHORITIES,
  FIELD_IS_BEING_SOLD,
  FIELD_IS_HERITAGE_LISTED,
  FIELD_IS_PROPERTY_OWNER,
  FIELD_IS_REGISTERED_PLACE,
  FIELD_MORTGAGEE_HAS_COMMENCED_PROCEEDINGS,
  FIELD_OWNERS_CORPORATION_RULES_ATTACHMENT,
  VALIDATION_MESSAGE_ATTACHMENT_REQUIRED,
  VALIDATION_MESSAGE_FURTHER_INFO_REQUIRED,
  VALIDATION_MESSAGE_REQUIRED,
} from 'app/pages/teams/onlist/components/Disclosures/constants'
import { disclosureVersionedQuestions } from 'app/pages/teams/onlist/components/Disclosures/questions'

interface DisclosureField {
  versions?: number[]
  conditionalOn?: string
  conditionalOnValue?: boolean
}

interface DisclosureVersionedQuestions {
  [key: string]: DisclosureField
}

interface DisclosureAnswers {
  [key: string]: boolean | string | null | undefined | any
}

interface ValidationErrors {
  [key: string]: string
}

interface ValidationResult {
  errors: ValidationErrors
  errorsPresented: boolean
}

interface DateFieldConfig {
  name: string
  conditionalOn: string | null
  required?: boolean
}

/**
 * Validates disclosure answers based on the disclosure version.
 * Uses the disclosureVersionedQuestions metadata to determine which fields are required for each version.
 *
 * @param answers - The disclosure answers object
 * @param version - The disclosure version (0, 1, 2, or 3)
 * @returns Object with { errors: {...}, errorsPresented: boolean }
 */
export const validateDisclosureAnswers = (
  answers: DisclosureAnswers,
  version: number,
): ValidationResult => {
  const errors: ValidationErrors = {}
  let errorsPresented = false

  // Cast to typed interface for safe access
  const questions = disclosureVersionedQuestions as DisclosureVersionedQuestions

  // Helper to check if a field is required for this version
  const isFieldInVersion = (fieldName: string): boolean => {
    const field = questions[fieldName]
    return Boolean(
      field && field.versions !== undefined && field.versions.includes(version),
    )
  }

  // Helper to check if a field should be conditionally validated
  const shouldValidateConditionalField = (fieldName: string): boolean => {
    const field = questions[fieldName]
    if (!field || !field.conditionalOn) {
      return true // Not a conditional field
    }

    const parentValue = answers[field.conditionalOn]

    // Check if there's a specific conditional value requirement
    if (field.conditionalOnValue !== undefined) {
      return parentValue === field.conditionalOnValue
    }

    // Default: validate if parent is true
    return parentValue === true
  }

  // Validate required boolean fields
  const booleanFields = [
    FIELD_HAS_HOMICIDE,
    FIELD_IS_REGISTERED_PLACE,
    FIELD_IS_AFFECTED_WITH_RELEVANT_AUTHORITIES,
    FIELD_HAS_ASBESTOS,
    FIELD_COMPLIES_WITH_MINIMUM_STANDARDS,
    FIELD_IS_BEING_SOLD,
    FIELD_HAS_EMBEDDED_NETWORK,
    // Version 3 fields
    FIELD_HAS_ENGAGED_AGENT_TO_SELL,
    FIELD_HAS_CONTRACT_OF_SALE_PREPARED,
    FIELD_HAS_MORTGAGEE_ACTION,
    FIELD_MORTGAGEE_HAS_COMMENCED_PROCEEDINGS,
    FIELD_IS_PROPERTY_OWNER,
    FIELD_HAS_RIGHT_TO_RENT,
    FIELD_HAS_MOULD_DAMP_REPAIR_NOTICE_3YR,
    FIELD_HAS_OUTSTANDING_SAFETY_RECOMMENDATIONS,
    FIELD_IS_HERITAGE_LISTED,
    FIELD_HAS_DRUG_USAGE_5YR,
    FIELD_HAS_DEFECT_SAFETY_CONCERN_NOTICE,
    FIELD_HAS_DOMESTIC_BUILDING_DISPUTE,
    FIELD_HAS_OWNERS_CORPORATION_DISPUTE,
    FIELD_HAS_OWNERS_CORPORATION_RULES,
  ]

  booleanFields.forEach((field) => {
    if (isFieldInVersion(field) && shouldValidateConditionalField(field)) {
      if (answers[field] === null || answers[field] === undefined) {
        errors[field] = VALIDATION_MESSAGE_REQUIRED
        errorsPresented = true
      }
    }
  })

  // Validate date fields (only for versions where they exist)
  const dateFields: DateFieldConfig[] = [
    { name: FIELD_DATE_OF_HOMICIDE, conditionalOn: FIELD_HAS_HOMICIDE },
    { name: FIELD_DATE_OF_REPAIR_NOTICE, conditionalOn: null },
    { name: FIELD_DATE_OF_DRUG_USAGE, conditionalOn: null },
    { name: FIELD_DATE_OF_GAS_CHECK, conditionalOn: null, required: false },
    {
      name: FIELD_DATE_OF_ELECTRICITY_CHECK,
      conditionalOn: null,
      required: false,
    },
    { name: FIELD_DATE_OF_POOL_CHECK, conditionalOn: null, required: false },
  ]

  dateFields.forEach(({ name, conditionalOn, required = true }) => {
    if (isFieldInVersion(name)) {
      const shouldValidate = conditionalOn
        ? answers[conditionalOn] === true
        : true
      if (shouldValidate && required && !answers[name]) {
        errors[name] = VALIDATION_MESSAGE_REQUIRED
        errorsPresented = true
      }
    }
  })

  // Validate embedded network fields (conditional on hasEmbeddedNetwork)
  if (answers[FIELD_HAS_EMBEDDED_NETWORK] === true) {
    const embeddedNetworkFields = [
      FIELD_EMBEDDED_NETWORK_NAME,
      FIELD_EMBEDDED_NETWORK_ABN,
      FIELD_EMBEDDED_NETWORK_PHONE,
      FIELD_EMBEDDED_NETWORK_PRICING,
      FIELD_EMBEDDED_NETWORK_WEBSITE,
    ]

    embeddedNetworkFields.forEach((field) => {
      if (isFieldInVersion(field) && !answers[field]) {
        errors[field] = VALIDATION_MESSAGE_REQUIRED
        errorsPresented = true
      }
    })
  }

  // Version 3 specific validations
  if (version === 3) {
    // Validate sub-questions when isBeingSold is true
    if (answers[FIELD_IS_BEING_SOLD] === true) {
      if (
        answers[FIELD_HAS_ENGAGED_AGENT_TO_SELL] === null ||
        answers[FIELD_HAS_ENGAGED_AGENT_TO_SELL] === undefined
      ) {
        errors[FIELD_HAS_ENGAGED_AGENT_TO_SELL] = VALIDATION_MESSAGE_REQUIRED
        errorsPresented = true
      }
      if (
        answers[FIELD_HAS_CONTRACT_OF_SALE_PREPARED] === null ||
        answers[FIELD_HAS_CONTRACT_OF_SALE_PREPARED] === undefined
      ) {
        errors[FIELD_HAS_CONTRACT_OF_SALE_PREPARED] =
          VALIDATION_MESSAGE_REQUIRED
        errorsPresented = true
      }
    }

    // Validate mortgagee sub-question
    if (answers[FIELD_HAS_MORTGAGEE_ACTION] === true) {
      if (
        answers[FIELD_MORTGAGEE_HAS_COMMENCED_PROCEEDINGS] === null ||
        answers[FIELD_MORTGAGEE_HAS_COMMENCED_PROCEEDINGS] === undefined
      ) {
        errors[FIELD_MORTGAGEE_HAS_COMMENCED_PROCEEDINGS] =
          VALIDATION_MESSAGE_REQUIRED
        errorsPresented = true
      }
    }

    // Validate has_right_to_rent when is_property_owner is false
    if (answers[FIELD_IS_PROPERTY_OWNER] === false) {
      if (
        answers[FIELD_HAS_RIGHT_TO_RENT] === null ||
        answers[FIELD_HAS_RIGHT_TO_RENT] === undefined
      ) {
        errors[FIELD_HAS_RIGHT_TO_RENT] = VALIDATION_MESSAGE_REQUIRED
        errorsPresented = true
      }
    }

    // Validate further_information is required when certain flags are true
    if (
      (answers[FIELD_HAS_OUTSTANDING_SAFETY_RECOMMENDATIONS] === true ||
        answers[FIELD_HAS_DEFECT_SAFETY_CONCERN_NOTICE] === true) &&
      (!answers[FIELD_FURTHER_INFORMATION] ||
        (typeof answers[FIELD_FURTHER_INFORMATION] === 'string' &&
          answers[FIELD_FURTHER_INFORMATION].trim() === ''))
    ) {
      errors[FIELD_FURTHER_INFORMATION] =
        VALIDATION_MESSAGE_FURTHER_INFO_REQUIRED
      errorsPresented = true
    }

    // Validate owners corporation rules attachment
    if (
      answers[FIELD_HAS_OWNERS_CORPORATION_RULES] === true &&
      !answers[FIELD_OWNERS_CORPORATION_RULES_ATTACHMENT]
    ) {
      errors[FIELD_OWNERS_CORPORATION_RULES_ATTACHMENT] =
        VALIDATION_MESSAGE_ATTACHMENT_REQUIRED
      errorsPresented = true
    }
  }

  return { errors, errorsPresented }
}

/**
 * Helper to get initial error state for disclosure form
 * @returns Initial errors object with empty strings
 */
export const getInitialDisclosureErrors = (): ValidationErrors => ({
  [FIELD_COMPLIES_WITH_MINIMUM_STANDARDS]: '',
  [FIELD_IS_REGISTERED_PLACE]: '',
  [FIELD_HAS_ASBESTOS]: '',
  [FIELD_IS_AFFECTED_WITH_RELEVANT_AUTHORITIES]: '',
  [FIELD_DATE_OF_HOMICIDE]: '',
  [FIELD_DATE_OF_REPAIR_NOTICE]: '',
  [FIELD_DATE_OF_GAS_CHECK]: '',
  [FIELD_DATE_OF_ELECTRICITY_CHECK]: '',
  [FIELD_DATE_OF_POOL_CHECK]: '',
  [FIELD_DATE_OF_DRUG_USAGE]: '',
  [FIELD_EMBEDDED_NETWORK_NAME]: '',
  [FIELD_EMBEDDED_NETWORK_ABN]: '',
  [FIELD_EMBEDDED_NETWORK_PHONE]: '',
  [FIELD_EMBEDDED_NETWORK_PRICING]: '',
  [FIELD_IS_BEING_SOLD]: '',
  [FIELD_HAS_HOMICIDE]: '',
  [FIELD_HAS_EMBEDDED_NETWORK]: '',
  [FIELD_EMBEDDED_NETWORK_WEBSITE]: '',
  // Version 3 fields
  [FIELD_HAS_ENGAGED_AGENT_TO_SELL]: '',
  [FIELD_HAS_CONTRACT_OF_SALE_PREPARED]: '',
  [FIELD_HAS_MORTGAGEE_ACTION]: '',
  [FIELD_MORTGAGEE_HAS_COMMENCED_PROCEEDINGS]: '',
  [FIELD_IS_PROPERTY_OWNER]: '',
  [FIELD_HAS_RIGHT_TO_RENT]: '',
  [FIELD_HAS_MOULD_DAMP_REPAIR_NOTICE_3YR]: '',
  [FIELD_HAS_OUTSTANDING_SAFETY_RECOMMENDATIONS]: '',
  [FIELD_IS_HERITAGE_LISTED]: '',
  [FIELD_HAS_DRUG_USAGE_5YR]: '',
  [FIELD_HAS_DEFECT_SAFETY_CONCERN_NOTICE]: '',
  [FIELD_HAS_DOMESTIC_BUILDING_DISPUTE]: '',
  [FIELD_HAS_OWNERS_CORPORATION_DISPUTE]: '',
  [FIELD_HAS_OWNERS_CORPORATION_RULES]: '',
  [FIELD_OWNERS_CORPORATION_RULES_ATTACHMENT]: '',
  [FIELD_FURTHER_INFORMATION]: '',
})
